/**
 * Speech-mode conversation UI — replaces the composer while voice mode is "speech".
 * Shows the phase indicator, live transcript, AI reply, and Stop/Pause controls.
 * Tapping the panel while the AI is speaking interrupts playback and reopens the mic.
 */
import { useEffect, useRef } from "react";
import { Mic, Pause, Play, Square, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useVoiceConversation, type ConversationPhase } from "@/hooks/useVoiceConversation";

interface AssistantMessageLike {
  id: string;
  content: string;
  status?: string;
  errorMessage?: string;
}

interface VoiceConversationPanelProps {
  active: boolean;
  isStreaming: boolean;
  lastAssistantMessage: AssistantMessageLike | null;
  onSendMessage: (text: string) => void;
  onExit: () => void;
  onNotice?: (message: string) => void;
  ttsVoiceId: string | null;
  ttsSpeed: number;
}

const PHASE_META: Record<ConversationPhase, { label: string; dotClass: string }> = {
  idle: { label: "Tap the mic to start talking", dotClass: "bg-muted-foreground" },
  listening: { label: "Listening...", dotClass: "bg-red-500 animate-pulse" },
  processing: { label: "Thinking...", dotClass: "bg-amber-500 animate-spin" },
  speaking: { label: "Speaking — tap anywhere to interrupt", dotClass: "bg-green-500 animate-pulse" },
  paused: { label: "Paused — tap to resume", dotClass: "bg-muted-foreground" },
  error: { label: "Something went wrong — tap to retry", dotClass: "bg-red-500" },
};

export function VoiceConversationPanel({
  active,
  isStreaming,
  lastAssistantMessage,
  onSendMessage,
  onExit,
  onNotice,
  ttsVoiceId,
  ttsSpeed,
}: VoiceConversationPanelProps) {
  const conversation = useVoiceConversation({
    active,
    isStreaming,
    lastAssistantMessage,
    onSendMessage,
    onExit,
    onNotice,
    ttsVoiceId,
    ttsSpeed,
  });

  const { phase, isSupported, userText, assistantText, errorMessage, start, pause, resume, interrupt, stop } =
    conversation;

  // Low-battery heads-up once per activation (voice loops are battery-hungry).
  const warnedBatteryRef = useRef(false);
  useEffect(() => {
    if (!active || warnedBatteryRef.current) return;
    const nav = navigator as Navigator & { getBattery?: () => Promise<{ level: number }> };
    if (typeof nav.getBattery !== "function") return;
    warnedBatteryRef.current = true;
    nav
      .getBattery()
      .then((battery) => {
        if (battery.level < 0.2) onNotice?.("Battery is low — voice conversation uses extra power.");
      })
      .catch(() => undefined);
  }, [active, onNotice]);

  const meta = PHASE_META[phase];

  const handlePanelTap = () => {
    if (phase === "speaking") interrupt();
    else if (phase === "paused" || phase === "error") resume();
    else if (phase === "idle") start();
  };

  if (!isSupported) {
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-6 text-center">
        <p className="text-sm text-muted-foreground">
          Voice conversation needs a browser with speech recognition (Chrome, Edge, or Safari).
        </p>
        <Button variant="outline" size="sm" onClick={stop} className="h-8 text-xs">
          Back to text input
        </Button>
      </div>
    );
  }

  return (
    /* The whole panel is tappable: start when idle, interrupt while speaking, resume when paused. */
    <div
      className="flex flex-col items-center gap-3 px-4 py-5 cursor-pointer select-none"
      onClick={handlePanelTap}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handlePanelTap();
        }
      }}
      aria-label={meta.label}
      data-testid="voice-conversation-panel"
    >
      <div className="relative flex items-center justify-center">
        {(phase === "listening" || phase === "speaking") && (
          <div
            className={`absolute inset-0 rounded-full opacity-25 animate-ping ${
              phase === "listening" ? "bg-red-500" : "bg-green-500"
            }`}
          />
        )}
        <div
          className={`relative z-10 flex h-14 w-14 items-center justify-center rounded-full text-white ${
            phase === "listening"
              ? "bg-red-500"
              : phase === "speaking"
                ? "bg-green-500"
                : phase === "processing"
                  ? "bg-amber-500"
                  : "bg-muted-foreground"
          }`}
        >
          {phase === "speaking" ? (
            <Volume2 className="h-6 w-6" />
          ) : phase === "paused" || phase === "idle" ? (
            <Play className="h-6 w-6" />
          ) : (
            <Mic className="h-6 w-6" />
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm font-medium" aria-live="polite">
        <span className={`h-2 w-2 rounded-full ${meta.dotClass}`} />
        {meta.label}
      </div>

      {errorMessage && <p className="text-xs text-destructive text-center max-w-md">{errorMessage}</p>}

      {userText && (
        <div className="w-full max-w-2xl rounded-lg border bg-muted/30 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">You</p>
          <p className="text-sm">{userText}</p>
        </div>
      )}

      {(assistantText || phase === "processing") && (
        <div className="w-full max-w-2xl rounded-lg border bg-background px-3 py-2">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5 flex items-center gap-1">
            AI {phase === "speaking" && <Volume2 className="h-3 w-3 text-green-500" />}
          </p>
          <p className="text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
            {assistantText || "Waiting for the response..."}
          </p>
        </div>
      )}

      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <Button
          variant="destructive"
          size="sm"
          onClick={stop}
          className="h-9 px-4 gap-1.5"
          data-testid="button-voice-stop"
          aria-label="Stop voice conversation"
        >
          <Square className="h-3.5 w-3.5 fill-current" />
          Stop
        </Button>
        {phase === "paused" || phase === "idle" || phase === "error" ? (
          <Button
            variant="default"
            size="sm"
            onClick={phase === "idle" ? start : resume}
            className="h-9 px-4 gap-1.5"
            data-testid="button-voice-resume"
            aria-label={phase === "idle" ? "Start conversation" : "Resume conversation"}
          >
            <Play className="h-3.5 w-3.5" />
            {phase === "idle" ? "Start" : "Resume"}
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={pause}
            className="h-9 px-4 gap-1.5"
            data-testid="button-voice-pause"
            aria-label="Pause conversation"
          >
            <Pause className="h-3.5 w-3.5" />
            Pause
          </Button>
        )}
      </div>
    </div>
  );
}
